import { Fragment, useMemo } from 'react';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import { convertCentsToDollars, parseAndFormatDate } from '../../../../shared/utils';
import FieldStatusChip from '../../../pets/pet-show/field-status-chip/field-status-chip';
import petShowStyle from '../../../pets/pet-show/pet-show-style';

function BillingFrequencyPetInfo({
  petData,
  displayStatuses,
  expandedRows,
  setExpandedRows,
  showWellness = false,
}) {
  const classes = petShowStyle();

  const memoizedPets = useMemo(() => {
    if (!petData || petData.length === 0) {
      return [];
    }
    return petData.map(pet => ({
      ...pet,
      // A wellness only pet has no policies at all
      latestDisplayedPolicy: (pet.policies ?? [])
        .filter(policy => displayStatuses.includes(policy.status))
        .sort((a, b) => b.year - a.year)[0],
      latestDisplayedWellness: (pet.wellness_plans ?? [])
        .filter(wellness => displayStatuses.includes(wellness.status))
        .sort((a, b) => b.year - a.year)[0],
      sortedPlans: pet.plans
        ? pet.plans
            .sort((a, b) => {
              return new Date(a.plan_end_date) - new Date(b.plan_end_date);
            })
            .map(plan => plan.id)
        : null,
    }));
  }, [petData, displayStatuses]);

  if (!petData || petData.length === 0) {
    return (
      <Typography sx={{ textAlign: 'center' }}>No valid pets to update.</Typography>
    );
  }

  function handleExpandRow(index) {
    setExpandedRows(prevExpandedRows =>
      prevExpandedRows.includes(index)
        ? prevExpandedRows.filter(row => row !== index)
        : [...prevExpandedRows, index]
    );
  }

  function renderTableHeaders() {
    return (
      <TableHead>
        <TableRow data-testid='billing-frequency-table-headers'>
          <TableCell sx={{ borderBottom: 'none' }}>Status</TableCell>
          <TableCell sx={{ borderBottom: 'none' }}>Year</TableCell>
          <TableCell sx={{ borderBottom: 'none' }}>Effective Date</TableCell>
          <TableCell sx={{ borderBottom: 'none' }}>Product</TableCell>
          <TableCell sx={{ borderBottom: 'none' }}>Version</TableCell>
          <TableCell sx={{ borderBottom: 'none' }}>Premium</TableCell>
          <TableCell sx={{ borderBottom: 'none' }}>Frequency</TableCell>
          <TableCell sx={{ borderBottom: 'none' }}>Discount</TableCell>
          <TableCell sx={{ borderBottom: 'none' }}>New Frequency</TableCell>
          <TableCell sx={{ borderBottom: 'none' }}>Amount owed</TableCell>
        </TableRow>
      </TableHead>
    );
  }

  function renderPolicyRow(pet) {
    const policy = pet.latestDisplayedPolicy;

    if (!policy) {
      return null;
    }

    return (
      <TableRow key={policy.id}>
        <TableCell sx={{ borderBottom: 'none' }}>
          <FieldStatusChip
            source='status'
            headerClassName={classes.colHeader}
            record={{ status: policy.status }}
          />
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>{policy.year}</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {parseAndFormatDate(policy.policy_effective_date)}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>Insurance</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>{policy.program_id}</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>${policy.premium}</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {policy.is_annualized_policy ? 'Annual' : 'Monthly'}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {policy.group_discount > 0 ? policy.group_discount : 'N/A'}
        </TableCell>
        <TableCell
          sx={{
            borderBottom: 'none',
            '::first-letter': { textTransform: 'capitalize' },
          }}
        >
          {pet.new_billing_frequency ?? ''}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {pet.policy_invoice_total && pet.policy_invoice_total > 0
            ? `$${convertCentsToDollars(pet.policy_invoice_total)}`
            : ''}
        </TableCell>
      </TableRow>
    );
  }

  function renderPetPlanRow(pet) {
    if (!displayStatuses.includes(pet.latest_plan?.status)) {
      return null;
    }

    const petPlan = pet.latest_plan;
    return (
      <TableRow key={petPlan.id}>
        <TableCell sx={{ borderBottom: 'none' }}>
          <FieldStatusChip
            source='status'
            headerClassName={classes.colHeader}
            record={{ status: petPlan.status }}
          />
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {pet.sortedPlans.indexOf(petPlan.id) + 1}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {parseAndFormatDate(petPlan.plan_effective_date)}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>{petPlan.plan.name}</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {petPlan.plan.description}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>${petPlan.cost}</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {petPlan.is_annualized_pet_plan ? 'Annual' : 'Monthly'}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>N/A</TableCell>
        <TableCell
          sx={{
            borderBottom: 'none',
            '::first-letter': { textTransform: 'capitalize' },
          }}
        >
          {pet.new_billing_frequency ?? ''}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {pet.pet_plan_invoice_total && pet.pet_plan_invoice_total > 0
            ? `$${convertCentsToDollars(pet.pet_plan_invoice_total)}`
            : ''}
        </TableCell>
      </TableRow>
    );
  }

  function renderWellnessRow(pet) {
    const wellness = pet.latestDisplayedWellness;

    if (!wellness) {
      return null;
    }

    return (
      <TableRow key={wellness.id}>
        <TableCell sx={{ borderBottom: 'none' }}>
          <FieldStatusChip
            source='status'
            headerClassName={classes.colHeader}
            record={{ status: wellness.status }}
          />
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>{wellness.year}</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {parseAndFormatDate(wellness.start_date)}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>Wellness</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>{wellness.program_id}</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>${wellness.price}</TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>
          {wellness.is_annualized ? 'Annual' : 'Monthly'}
        </TableCell>
        <TableCell sx={{ borderBottom: 'none' }}>N/A</TableCell>
        <TableCell
          sx={{
            borderBottom: 'none',
            '::first-letter': { textTransform: 'capitalize' },
          }}
        >
          {pet.new_billing_frequency ?? ''}
        </TableCell>
        {/* Renewal billing frequency changes raise no catch-up invoice */}
        <TableCell sx={{ borderBottom: 'none' }} />
      </TableRow>
    );
  }

  return (
    <TableBody>
      {memoizedPets.map((pet, index) => (
        <Fragment key={pet.id}>
          <TableRow
            sx={{
              backgroundColor: 'inherit',
            }}
          >
            <TableCell
              style={{ paddingLeft: 35 }}
              data-testid='billing-frequency-pet-table-name'
            >
              {pet.name}
            </TableCell>
            <TableCell sx={{ textAlign: 'right' }}>
              <IconButton
                onClick={() => handleExpandRow(index)}
                aria-expanded={expandedRows.includes(index)}
                aria-label='expand row'
              >
                {expandedRows.includes(index) ? (
                  <ExpandLessIcon />
                ) : (
                  <ExpandMoreIcon />
                )}
              </IconButton>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
              <Collapse
                in={expandedRows.includes(index)}
                timeout='auto'
                unmountOnExit
              >
                <Box margin={1}>
                  <Table size='small'>
                    {renderTableHeaders()}
                    <TableBody key={pet.id}>{renderPolicyRow(pet)}</TableBody>
                    {pet.plans && pet.plans.length > 0 ? (
                      <TableBody>{renderPetPlanRow(pet)}</TableBody>
                    ) : null}
                    {showWellness && pet.latestDisplayedWellness ? (
                      <TableBody>{renderWellnessRow(pet)}</TableBody>
                    ) : null}
                  </Table>
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        </Fragment>
      ))}
    </TableBody>
  );
}
BillingFrequencyPetInfo.propTypes = {
  petData: PropTypes.array,
  displayStatuses: PropTypes.arrayOf(PropTypes.string),
  expandedRows: PropTypes.arrayOf(PropTypes.number),
  setExpandedRows: PropTypes.func,
  showWellness: PropTypes.bool,
};
export default BillingFrequencyPetInfo;
